import { CSSProperties, useCallback, useMemo, useState } from "react";
import { useTheme } from "styled-components";
import { CircleAlert, CircleCheck } from "lucide-react";
import { getDisplayResultMessageFunction } from "@shared/utils/misc";
import { displayResultMessage, openMessage } from "utils/misc";
import { AuthInlineMessage } from "features/auth/styles";

/**
 * The result message for an auth request. Classic: toasts, as before. New
 * look: a failed request's message shows inline in the form (next to what
 * the person has to fix) instead of a toast; success messages still toast,
 * or show inline too with `inlineSuccess` (when the person stays on the step).
 * Each new request clears the previous messages.
 */
export const useAuthResultMessage = ({
  inlineSuccess = false,
}: { inlineSuccess?: boolean } = {}) => {
  const { isNewLook } = useTheme();
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const display = useMemo(() => {
    if (!isNewLook) return displayResultMessage;
    const inline = getDisplayResultMessageFunction((text, type, duration) => {
      if (type === "error") setError(text);
      else if (inlineSuccess) setSuccess(text);
      else openMessage(text, type, duration);
    });
    return ((promise, ...rest) => {
      setError(undefined);
      setSuccess(undefined);
      return inline(promise, ...rest);
    }) as typeof displayResultMessage;
  }, [isNewLook, inlineSuccess]);
  const clearError = useCallback(() => setError(undefined), []);
  return { display, error, success, clearError };
};

/** New look: a form-level error (or success), announced as it appears. */
const AuthInlineError = ({
  message,
  type = "error",
  style,
}: {
  message?: string;
  type?: "error" | "success";
  style?: CSSProperties;
}) => {
  if (!message) return null;
  const Icon = type === "error" ? CircleAlert : CircleCheck;
  return (
    <AuthInlineMessage
      role={type === "error" ? "alert" : "status"}
      $type={type}
      data-testid={`auth-inline-${type}`}
      style={style}
    >
      <Icon size={16} strokeWidth={1.75} aria-hidden />
      <span>{message}</span>
    </AuthInlineMessage>
  );
};

export default AuthInlineError;
